import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { AiClientContext, AiPageContext } from '../contracts/ai-context.types';
import { normalizeUiLocale } from '../localization';

/**
 * Derives an `AiClientContext` from the current browser URL and React Router
 * params — surfaces the previously-unused `AiPageContext`/`AiClientContext`
 * contract that the backend already accepts but was never populated.
 *
 * Entity extraction is opportunistic: if the URL contains a numeric segment
 * after a known entity-type keyword, we surface it. No routes are hard-coded
 * here — the pattern match works for any `/:entityType/:id` shape.
 */
export const useAiPageContext = (): AiClientContext => {
  const location = useLocation();
  const params = useParams<Record<string, string>>();
  const { i18n } = useTranslation();
  const locale = normalizeUiLocale(i18n.resolvedLanguage ?? i18n.language);

  return useMemo((): AiClientContext => {
    const page: AiPageContext = {
      route: location.pathname,
    };

    // Heuristic: look for a numeric path segment preceded by a known entity noun.
    // e.g. /admin/orders/1234  → entity = { type: 'orders', id: '1234' }
    //      /admin/warehouses/42 → entity = { type: 'warehouses', id: '42' }
    const ENTITY_TYPES = [
      'orders',
      'order',
      'warehouses',
      'warehouse',
      'stock-owners',
      'stock-owner',
      'products',
      'product',
      'inventory',
    ];

    const segments = location.pathname.split('/').filter(Boolean);
    for (let i = 0; i < segments.length - 1; i++) {
      const segment = segments[i];
      const next = segments[i + 1];
      if (ENTITY_TYPES.includes(segment) && /^\d+$/.test(next)) {
        page.entity = { type: segment, id: next };
        break;
      }
    }

    // Also check React Router params for named :id params (e.g. :warehouseId)
    if (!page.entity) {
      for (const [key, value] of Object.entries(params)) {
        if (value && /^\d+$/.test(value) && key.toLowerCase().endsWith('id')) {
          const typeName = key.slice(0, -2); // strip trailing 'Id' / 'ID'
          page.entity = { type: typeName, id: value };
          break;
        }
      }
    }

    return {
      locale,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      page,
    };
  }, [locale, location.pathname, params]);
};
